import styled, { keyframes, RuleSet, css } from "styled-components";
import { pulseDarker } from "../tables/LoadingTableCell";

export const RowContainer = styled.div<{
  flex?: string;
  jc?: string;
  ai?: string;
  mt?: number;
  mb?: number;
  ml?: number;
  mr?: number;
  wrap?: string;
  w?: string;
}>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props): string =>
    props.jc ? props.jc : "space-between"};
  align-items: ${({ ai }): string => (ai ? ai : "baseline")};
  margin-bottom: ${({ mb }): number => (mb ? mb : 10)}px;
  margin-top: ${({ mt }): number => (mt ? mt : 10)}px;
  margin-left: ${({ ml }): number => (ml ? ml : 0)}px;
  margin-right: ${({ mr }): number => (mr ? mr : 0)}px;
  width: ${({ w }): string => (w ? w : "100%")};
  flex: ${({ flex }): string => (flex ? flex : "none")};
  flex-wrap: ${({ wrap }): string => (wrap ? wrap : "nowrap")};
`;

export const ColumnContainer = styled.div<{
  jc?: string;
  mt?: number;
  mb?: number;
  ml?: number;
  ai?: string;
  w?: string;
  bg?: string;
}>`
  display: flex;
  width: ${({ w }): string => (w ? w : "100%")};
  flex-grow: 1;
  flex-direction: column;
  justify-content: ${({ jc }): string => (jc ? jc : "center")};
  align-items: ${({ ai }): string => (ai ? ai : "center")};
  margin-top: ${({ mt }): number => (mt ? mt : 10)}px;
  margin-bottom: ${({ mb }): number => (mb ? mb : 0)}px;
  margin-left: ${({ ml }): number => (ml ? ml : 0)}px;
  background-color: ${({ bg }): string => (bg ? bg : "transparent")};
`;

export const ElementContainer = styled.div<{
  flexDirection?: string;
  ai?: string;
  mt?: number;
  mb?: number;
  bg?: string;
  br?: number;
  mr?: number;
  ml?: number;
  h?: string;
  w?: string;
  jc?: string;
  p?: number;
  pt?: number;
  cursor?: string;
  bb?: string;
  f?: number;
  isLoading?: boolean;
}>`
  display: flex;
  ${({ f }): string => (f ? `flex: ${f}` : "")};
  flex-direction: ${({ flexDirection }): string =>
    flexDirection ? flexDirection : "row"};
  justify-content: ${({ jc }): string => (jc ? jc : "flex-start")};
  align-items: ${({ ai }): string => (ai ? ai : "baseline")};
  margin-right: ${({ mr }): number => (mr ? mr : 10)}px;
  margin-left: ${({ ml }): number => (ml ? ml : 0)}px;
  margin-top: ${({ mt }): number => (mt ? mt : 0)}px;
  margin-bottom: ${({ mb }): number => (mb ? mb : 0)}px;
  background-color: ${({ bg }): string => (bg ? bg : "transparent")};
  border-radius: ${({ br }): number => (br ? br : 0)}px;
  height: ${({ h }): string => (h ? h : "auto")};
  width: ${({ w }): string => (w ? w : "auto")};
  padding: ${({ p }): number => (p ? p : 0)}px;
  padding-top: ${({ pt }): number => (pt ? pt : 0)}px;
  cursor: ${({ cursor }): string => (cursor ? cursor : "auto")};
  border-bottom: ${({ bb }): string => (bb ? bb : "none")};
  ${({ isLoading }): false | RuleSet<object> | undefined =>
    isLoading &&
    css`
      animation: ${pulseDarker} 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    `}
`;

export const HorizontalLine = styled.div<{
  marginTop?: number;
  marginBottom?: number;
  color?: string;
  p?: string;
  w?: string;
  thickness?: number;
}>`
  height: ${({ thickness }): number => (thickness ? thickness : 1)}px;
  width: ${({ w }): string => (w ? w : "100%")};
  background-color: ${({ color }): string => (color ? color : "#ccc")};
  margin: 0;
  margin-top: ${(props): number => (props.marginTop ? props.marginTop : 35)}px;
  margin-bottom: ${(props): number =>
    props.marginBottom ? props.marginBottom : 0}px;
  padding: ${({ p }): string => (p ? p : "0")};
`;

export const VerticalLine = styled.div<{
  margin?: string;
  color?: string;
  height?: number;
}>`
  width: 2px;
  height: ${({ height }): number => (height ? height : 25)}px;
  background-color: ${({ color }): string => (color ? color : "#ccc")};
  margin: 0;
  margin-left: ${(props): string => (props.margin ? props.margin : "5px")};
  border-radius: 4px;
`;

export const Column = styled.div<{ flex: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end; // Ensures children, like LoginBox, align to bottom
  height: 100%;
  flex: ${({ flex }): string => flex}; // Accepts flex prop to determine size
`;

export const pulse = keyframes`
  0% {
    opacity: 0.7;
  }
  40% {
    opacity: 0.5;
  }
  100% {
    opacity: 0.7;
  }
`;

export const Card = styled.div<{
  h?: string;
  w?: number;
  wpx?: string;
  m?: string;
  isLoading?: boolean;
  p?: string;
  ovf?: string;
  bg?: string;
}>`
  position: relative;
  background-color: ${({ isLoading }): string =>
    isLoading ? "#D3D3D3" : "white"};
  border-radius: 10px;
  padding: ${({ p }): string => (p ? p : "20px")};
  margin: ${({ m }): string => (m ? m : "12px")};
  font-family: "Apercu";
  height: ${({ h }): string => (h ? h : "250px")};
  width: ${({ w }): number => (w ? w : 100)}%;
  ${({ wpx }): string | undefined => (wpx ? `width: ${wpx};` : undefined)}
  ${({ bg }): string | undefined =>
    bg ? `background-color: ${bg};` : undefined}
  min-width: 0;
  overflow: ${({ ovf }): string => (ovf ? ovf : "visible")};
  opacity: ${({ isLoading }): string => (isLoading ? "0.5" : "1")};
  ${({ isLoading }): false | RuleSet<object> | undefined =>
    isLoading &&
    css`
      animation: ${pulse} 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    `}
`;

export const BackgroundContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
`;

export const NavBarContainer = styled.span`
  position: sticky;
  top: 0;
  box-sizing: border-box;
  background-color: #f5f5f5;
  width: 100%;
  z-index: 5;
`;

export const MainColumn = styled(Column)<{
  show?: boolean;
  bg?: string;
  overflow?: boolean;
}>`
  transition: 0.15s ease;
  min-height: 100vh;
  overflow: hidden; // Prevents content from overflowing during transition
  min-width: ${({ show }): string => (show ? "300px" : "60px")};
  background-color: ${({ bg }): string => bg || "white"};
  justify-content: flex-start;
  align-items: flex-start;
  overflow-y: ${({ overflow }): string => (overflow ? "auto" : "hidden")};
`;

export const BoxWithShadow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding-bottom: 20px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
`;

export const FlexibleContainer = styled.div<{
  m?: string;
  p?: string;
  w?: string;
  h?: string;
  mh?: string;
  jc?: string;
  ai?: string;
  fd?: string;
  bg?: string;
  br?: string;
  mt?: string;
  mb?: string;
  ml?: string;
  mr?: string;
  c?: string;
  ofx?: string;
  ofy?: string;
  fw?: string;
  fg?: number;
  as?: string;
  f?: number;
  isLoading?: boolean;
}>`
  display: flex;
  ${({ f }): string => (f ? `flex: ${f}` : "")};
  flex-direction: ${({ fd }): string => (fd ? fd : "row")};
  justify-content: ${({ jc }): string => (jc ? jc : "center")};
  align-items: ${({ ai }): string => (ai ? ai : "center")};
  margin-top: ${({ mt }): string => (mt ? mt : "0px")};
  margin-bottom: ${({ mb }): string => (mb ? mb : "0px")};
  margin-left: ${({ ml }): string => (ml ? ml : "0px")};
  margin-right: ${({ mr }): string => (mr ? mr : "0px")};
  padding: ${({ p }): string => (p ? p : "0px")};
  width: ${({ w }): string => (w ? w : "auto")};
  height: ${({ h }): string => (h ? h : "auto")};
  ${({ mh }): string => (mh ? `max-height: ${mh}` : "")};
  background-color: ${({ bg }): string => (bg ? bg : "transparent")};
  border-radius: ${({ br }): string => (br ? br : "0px")};
  cursor: ${({ c }): string => (c ? c : "auto")};
  overflow-x: ${({ ofx }): string => (ofx ? ofx : "visible")};
  overflow-y: ${({ ofy }): string => (ofy ? ofy : "visible")};
  flex-wrap: ${({ fw }): string => (fw ? fw : "nowrap")};
  flex-grow: ${({ fg }): number => (fg ? fg : 0)};
  align-self: ${({ as }): string => (as ? as : "auto")};
  ${({ isLoading }): false | RuleSet<object> | undefined =>
    isLoading &&
    css`
      animation: ${pulseDarker} 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    `}
`;
